import {
  OfferAvailability,
  OfferAvailabilityByProductType,
} from "./OfferAvailability.type";
import {
  IDeliverability,
  IDeliverabilityStatus,
} from "../../model/IDeliverability";
import { ILPGOffer } from "../../model/ILPGOffer";

const updateBasedOnOther = (
  tab: OfferAvailability,
  otherTab: OfferAvailability
): OfferAvailability => {
  if (otherTab === OfferAvailability.OFFERS_AVAILABLE) {
    if (tab === OfferAvailability.ERROR) {
      return OfferAvailability.ERROR_OTHER_AVAILABLE;
    }
  }

  if (otherTab === OfferAvailability.DELIVER_ALTERNATE) {
    if (tab === OfferAvailability.UNDELIVERABLE) {
      return OfferAvailability.OTHER_DELIVER_ALTERNATE;
    }
  }

  // When the other type has no offers at all, a non-deliverable status on this
  // side has no actionable path — fall back to ERROR so UnableToSignup is shown.
  if (otherTab === OfferAvailability.ERROR) {
    if (
      tab === OfferAvailability.UNDELIVERABLE ||
      tab === OfferAvailability.DELIVER_ALTERNATE
    ) {
      return OfferAvailability.ERROR;
    }
  }

  return tab;
};

const considerDeliverability = (
  deliverability: IDeliverabilityStatus,
  offer: OfferAvailability
): OfferAvailability => {
  switch (deliverability) {
    // if deliverable then make sure there are offers to show
    case IDeliverabilityStatus.DELIVERABLE:
      return offer;
    // if not deliverable then we can always show deliverability status regardless of offers
    case IDeliverabilityStatus.DELIVER_ALTERNATE:
    case IDeliverabilityStatus.DELIVER_SUPPLIER:
      return OfferAvailability.DELIVER_ALTERNATE;
    case IDeliverabilityStatus.UNDELIVERABLE:
      return OfferAvailability.UNDELIVERABLE;
    default:
      /* istanbul ignore next */
      throw new Error(
        `Unsupported deliverability status [${deliverability}] while considering deliverability in offer availability.`
      );
  }
};

const considerHavingOffers = (offers: Array<ILPGOffer>): OfferAvailability => {
  const hasOffers = offers.length > 0;
  if (hasOffers) {
    return OfferAvailability.OFFERS_AVAILABLE;
  }
  return OfferAvailability.ERROR;
};

const determineByOffers = (
  offers: Array<ILPGOffer>
): OfferAvailabilityByProductType => {
  const exchangeOffers = offers.filter((offer) => !offer.isRefill);
  const refillOffers = offers.filter((offer) => offer.isRefill);
  const exchangeAvailability = considerHavingOffers(exchangeOffers);
  const refillAvailability = considerHavingOffers(refillOffers);
  return {
    exchange: updateBasedOnOther(exchangeAvailability, refillAvailability),
    refill: updateBasedOnOther(refillAvailability, exchangeAvailability),
  };
};

const determineByDeliverability = (
  deliverability: IDeliverability,
  offers: Array<ILPGOffer>
): OfferAvailabilityByProductType => {
  const availabilityByOffers = determineByOffers(offers);
  const exchangeAvailability = considerDeliverability(
    deliverability.exchange.status,
    availabilityByOffers.exchange
  );
  const refillAvailability = considerDeliverability(
    deliverability.refill.status,
    availabilityByOffers.refill
  );
  return {
    exchange: updateBasedOnOther(exchangeAvailability, refillAvailability),
    refill: updateBasedOnOther(refillAvailability, exchangeAvailability),
  };
};

export const determineOfferAvailability = (
  offers: Array<ILPGOffer>,
  deliverability?: IDeliverability
): OfferAvailabilityByProductType => {
  if (!deliverability) {
    return determineByOffers(offers);
  }
  return determineByDeliverability(deliverability, offers);
};
